import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import qs from 'query-string'

type QueryValue = string | number | boolean | undefined | null

interface UseQueryParamsResult {
  query: qs.ParsedQuery<string>
  getParam: (key: string) => string | undefined | null
  setQueryParams: (updates: Record<string, QueryValue>) => void
  setQueryParam: (key: string, value: QueryValue) => void
}

export const useQueryParams = (): UseQueryParamsResult => {
  const location = useLocation()
  const navigate = useNavigate()

  const query = useMemo(() => qs.parse(location.search), [location.search])

  const setQueryParams = useCallback(
    (updates: Record<string, QueryValue>) => {
      const current = qs.parse(location.search)
      const next: qs.ParsedQuery<string> = { ...current }

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete next[key]
        } else {
          next[key] = String(value)
        }
      })

      const searchString = qs.stringify(next, {
        skipEmptyString: true,
        skipNull: true
      })

      navigate(
        {
          pathname: location.pathname,
          search: searchString ? `?${searchString}` : ''
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate]
  )

  const setQueryParam = useCallback(
    (key: string, value: QueryValue) => setQueryParams({ [key]: value }),
    [setQueryParams]
  )

  const getParam = useCallback(
    (key: string): string | undefined | null => {
      const value = query[key]

      if (Array.isArray(value)) {
        return value[0]
      }

      return value?.toString()
    },
    [query]
  )

  return { query, getParam, setQueryParams, setQueryParam }
}
